import { useEffect, useState } from "react";
import { Search, ArrowLeft, Sparkles, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORY_CHIPS, haversineKm, fmtDistanceWalk } from "../lib/geo";
import { useGps } from "../lib/useGps";
import { PlaceRow } from "../components/PlaceCard";
import { BottomNav } from "../components/BottomNav";
import { Switch } from "../components/ui/switch";

export default function Places() {
  const [category, setCategory] = useState("all");
  const [openNow, setOpenNow] = useState(false);
  const [vipOnly, setVipOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const { coords } = useGps();

  useEffect(() => {
    const params = new URLSearchParams();
    if (category !== "all") params.set("category", category);
    if (openNow) params.set("open_now", "true");
    if (vipOnly) params.set("vip", "true");
    if (search) params.set("q", search);
    setLoading(true);
    api.get(`/businesses?${params.toString()}`).then(({ data }) => setRows(data || [])).finally(() => setLoading(false));
  }, [category, openNow, vipOnly, search]);

  const distanceFor = (b) => {
    if (!coords || b.lat == null || b.lng == null) return null;
    return fmtDistanceWalk(haversineKm(coords, { lat: b.lat, lng: b.lng }));
  };

  return (
    <main data-testid="places-page" className="min-h-screen pb-32 bg-background">
      <header className="px-5 pt-6 pb-3 flex items-center gap-3">
        <Link to="/" data-testid="places-back" className="btn-ghost p-2"><ArrowLeft className="w-4 h-4" strokeWidth={1.5} /></Link>
        <div>
          <div className="eyebrow">Directory</div>
          <h1 className="font-display text-3xl mt-1 text-foreground">All places</h1>
        </div>
      </header>

      <div className="px-5">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40" strokeWidth={1.5} />
          <input
            data-testid="places-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            className="form-input pl-11 rounded-full text-sm"
          />
        </div>
      </div>

      <div className="mt-4 overflow-x-auto no-scrollbar px-5" data-testid="places-chips">
        <div className="flex gap-2 min-w-max">
          {CATEGORY_CHIPS.map((c) => {
            const active = c.key === category;
            return (
              <button key={c.key} data-testid={`places-chip-${c.key}`} onClick={() => setCategory(c.key)}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest font-medium border transition-colors ${
                  active ? "bg-gold text-[hsl(var(--primary-foreground))] border-transparent" : "border-gold-soft text-foreground/70 hover:border-gold-strong hover:text-gold"
                }`}>{c.label}</button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 px-5 flex items-center gap-3" data-testid="places-toggles">
        <FilterToggle label="Open now" tid="filter-open-now" icon={Sparkles} checked={openNow} onChange={setOpenNow} />
        <FilterToggle label="VIP" tid="filter-vip" icon={Crown} checked={vipOnly} onChange={setVipOnly} />
        <span className="ml-auto text-xs text-foreground/45 font-mono" data-testid="places-count">{rows.length} {rows.length === 1 ? "place" : "places"}</span>
      </div>

      <section className="mt-4 px-3 sm:px-5">
        {loading ? (
          <div className="py-12 text-center text-foreground/45 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-foreground/45 text-sm" data-testid="places-empty">No places match your filters.</div>
        ) : (
          <div className="divide-y divide-[rgba(232,184,75,0.12)]">
            {rows.map((b) => <PlaceRow key={b.id} b={b} distance={distanceFor(b)} />)}
          </div>
        )}
      </section>

      <BottomNav />
    </main>
  );
}

function FilterToggle({ label, tid, icon: Icon, checked, onChange }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-full border text-xs ${checked ? "border-gold-strong text-gold bg-[rgba(232,184,75,0.08)]" : "border-gold-soft text-foreground/65"}`}>
      <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
      <span className="uppercase tracking-widest">{label}</span>
      <Switch data-testid={tid} checked={checked} onCheckedChange={onChange} className="scale-75" />
    </div>
  );
}
